'use client';

import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ExpandableCallout, Text, TextInput } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupIdentityStyle } from '../components/signup-identity-style';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { REGISTRATION_PLAN_ID_MAX_LENGTH } from '@galapagos/shared';
import {
  planDetailsFromInput,
  planDetailsInputErrors,
  type PlanDetailsInputErrors,
} from '../health-plan/plan-details';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';

const MEMBER_ID_INPUT_ID = 'signup-plan-details-member-id';
const GROUP_NUMBER_INPUT_ID = 'signup-plan-details-group-number';
const NO_ERRORS: PlanDetailsInputErrors = { memberId: null, groupNumber: null };

function PlanDetailsStep() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const { t } = useTranslation('signup');
  const title = t('planDetails.title');
  const headingRef = useStepHeading(title);
  const { goToNextStep } = useSignupNavigation();
  const saved = useSignupFlowStore((state) => state.planDetails);
  const setPlanDetails = useSignupFlowStore((state) => state.setPlanDetails);
  const [memberId, setMemberId] = useState(saved?.memberId ?? '');
  const [groupNumber, setGroupNumber] = useState(saved?.groupNumber ?? '');
  const [errors, setErrors] = useState<PlanDetailsInputErrors>(NO_ERRORS);
  const formRef = useRef<HTMLFormElement>(null);
  ensureSignupStepStyle();
  ensureSignupIdentityStyle();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = planDetailsInputErrors(memberId, groupNumber);
    const details = planDetailsFromInput(memberId, groupNumber);
    if (found.memberId !== null || found.groupNumber !== null || details === null) {
      setErrors(found);
      const firstInvalid =
        found.groupNumber !== null && found.memberId === null
          ? GROUP_NUMBER_INPUT_ID
          : MEMBER_ID_INPUT_ID;
      formRef.current?.querySelector<HTMLInputElement>(`[data-testid="${firstInvalid}"]`)?.focus();
      return;
    }
    // Sent later with the contact step's profile save; nothing is sent from here.
    setPlanDetails(details);
    goToNextStep('plan-details');
  };

  return (
    <SignupShell>
      <form ref={formRef} className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle title={title} headingRef={headingRef} />
        <ExpandableCallout
          title={t('planDetails.whyTitle')}
          actionId="signup.plan-details-screen-why.toggle"
          data-testid="signup-plan-details-why"
        >
          <Text variant="b3Regular" value={t('planDetails.whyBody')} />
        </ExpandableCallout>
        {/* Plan identifiers are PHI: kept out of autocapture, replay and click events. */}
        <PrivateFields>
          <div className="signup-fields">
            <TextInput
              label={t('planDetails.memberIdLabel')}
              helperText={t('planDetails.memberIdHelper')}
              value={memberId}
              onChangeText={(next) => {
                setMemberId(next);
                if (errors.memberId !== null) setErrors({ ...errors, memberId: null });
              }}
              autoComplete="off"
              name="plan-member-id"
              errorText={
                errors.memberId === 'required'
                  ? t('planDetails.memberIdRequired')
                  : errors.memberId === 'tooLong'
                    ? t('planDetails.tooLongError', { max: REGISTRATION_PLAN_ID_MAX_LENGTH })
                    : undefined
              }
              data-testid={MEMBER_ID_INPUT_ID}
            />
            <TextInput
              label={t('planDetails.groupNumberLabel')}
              helperText={t('planDetails.groupNumberHelper')}
              value={groupNumber}
              onChangeText={(next) => {
                setGroupNumber(next);
                if (errors.groupNumber !== null) setErrors({ ...errors, groupNumber: null });
              }}
              autoComplete="off"
              name="plan-group-number"
              errorText={
                errors.groupNumber === 'tooLong'
                  ? t('planDetails.tooLongError', { max: REGISTRATION_PLAN_ID_MAX_LENGTH })
                  : undefined
              }
              data-testid={GROUP_NUMBER_INPUT_ID}
            />
          </div>
        </PrivateFields>
        <PrivacyNote text={t('planDetails.privacyNote')} />
        {/* The step's one status announcer; field errors are announced by their inputs. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status" />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            htmlType="submit"
            actionId="signup.plan-details-screen-continue.activate"
            data-testid="signup-plan-details-continue"
          />
        </FormActions>
      </form>
    </SignupShell>
  );
}

/** `/plan-details/`: the health-plan member ID and group number (v9 1.5-A.1, production H_insurance). */
export function PlanDetailsStepScreen() {
  return (
    <EarlierAnswersGate step="plan-details">
      <PlanDetailsStep />
    </EarlierAnswersGate>
  );
}
